<template>
  <div
    v-if="weatherData.length > 0"
    class="w-12/12 my-4 p-0 md:mt-5"
  >
    <h2
      v-if="title && !hideText"
      class="font-secondary flex mt-4 mb-4 md:mb-4"
      :data-i18n="title"
      :style="{ fontSize: isTabView ? '2.5rem' : '' }"
    >
      {{ $t(title) }}
    </h2>
    <p
      v-if="!hideText"
      class="flex mt-4 mb-4 md:mb-4"
    >
      {{ content.description ? content.description : $t('charterDestinationWeatherSubtext') }}
    </p>
    <Swiper
      class="charter-destination__weather-slider mt-4 md:mt-5 md:-mx-3"
      v-bind="sliderOptions"
    >
      <div
        v-for="(weather, index) in weatherData"
        :key="index"
        class="flex justify-center"
      >
        <div class="charter-destination__weather w-full">
          <h4 class="transform-uppercase text-center mb-1">
            {{ month(weather.month) }}
          </h4>
          <div class="text-center mb-1">
            <img
              :src="`/weather/${weather.icon}.png`"
              :alt="weather.icon"
            />
            <div
              v-for="{ icon, label, text } in filteredWeatherList(weather)"
              :key="label"
              class="mb-4"
            >
              <small :data-i18n="label">
                {{ $t(label) }}
              </small>
              <div class="flex items-center weather-item">
                <fa
                  class="fa-lg"
                  :icon="icon"
                />
                <span class="font-bold h3 ml-2 mb-0">
                  {{ text }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Swiper>
  </div>
</template>

<script>
import {
  faSwimmingPool,
  faThermometerThreeQuarters,
  faSun,
  faSunCloud,
  faWind,
} from '@fortawesome/pro-duotone-svg-icons'

const icons = {
  faSwimmingPool,
  faThermometerThreeQuarters,
  faSun,
  faSunCloud,
  faWind,
}

export default defineNuxtComponent({
  setup () {
    const { localeDate } = useDate()
    const dayjs = useDayjs()

    return {
      localeDate,
      dayjs,
    }
  },

  props: {
    title: {
      type: String,
      default: 'Väder',
    },

    content: {
      type: Object,
      default: () => ({}),
    },

    hideText: {
      type: Boolean,
      default: false,
    },

    isTabView: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      icons: {
        faSwimmingPool,
        faThermometerThreeQuarters,
        faSun,
        faSunCloud,
        faWind,
      },

      sliderOptions: {
        verticalCenteredSlides: false,
        pagination: false,
        arrowsType: 'gray-hover',
        constantDisplayNavigation: true,
        rewind: true,

        spaceBetween: 140,

        slidesPerView: 2,
        initialSlide: this.localeDate(undefined, 'M') - 1,

        breakpoints: this.$isSol ? {
          300: {
            slidesPerView: 2,
            spaceBetween: 60,
          },

          568: {
            slidesPerView: 2,
            spaceBetween: 100,
          },

          [getTailwindBreakpointsPx().md]: {
            slidesPerView: 3,
          },

          [getTailwindBreakpointsPx().xl]: {
            slidesPerView: 4,
          },
        } : {
          620: {
            slidesPerView: 3,
          },

          820: {
            slidesPerView: 4,
          },

          [getTailwindBreakpointsPx().lg]: {
            slidesPerView: 4,
          },

          1280: {
            slidesPerView: 5,
          },

          1800: {
            slidesPerView: 5,
          },
        },
      },
    }
  },

  computed: {
    weatherData () {
      return this.content.data || []
    },
  },

  methods: {
    filteredWeatherList (weather) {
      return this.list(weather).filter(({ condition }) => condition)
    },

    list (w) {
      return [
        {
          label: 'charterDestinationWeather.DayTemperature',
          icon: icons.faThermometerThreeQuarters,
          text: `${w.temp_from}-${w.temp_to}°C`,
        },
        {
          label: 'charterDestinationWeather.SeaTemperature',
          icon: icons.faSwimmingPool,
          text: `${w?.sea_temperature}°C`,
          condition: w?.sea_temperature,
        },
        {
          label: 'charterDestinationWeather.SunDays',
          icon: icons.faSun,
          text: w?.sunny_days,
          condition: w?.sunny_days,
        },
        {
          label: 'charterDestinationWeather.RainfreeDays',
          icon: icons.faSunCloud,
          text: w?.rainfree_days,
          condition: w?.rainfree_days,
        },
        {
          label: 'charterDestinationWeather.Wind',
          icon: icons.faWind,
          text: `${w?.windspeed} m/s`,
          condition: w?.windspeed,
        },
      ]
    },

    month (index) {
      return this.dayjs.monthsShort()[index - 1]
    },

    getIcon (icon) {
      const iconClass = 'fa' + icon.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('')

      return icons[iconClass] || ['fad', icon]
    },
  },
})
</script>

<style lang="scss" scoped>
.charter-destination__weather {
  background-color: theme('colors.light-turquoise');
}
.weather-item {
  & > svg.fa-lg {
    width: 25px;
  }
}
</style>
